// filter.js
const dataPrefix = 'data-filter';
const classPrefix = 'filter';

function _attr(name) {
    return `${dataPrefix}-${name}`;
}

/**
 * Инициализация фильтра.
 * options:
 *   type: 'list' | 'single' | 'switch'
 *   filterFor: string
 *   title: string
 *   name: string
 *   data: массив опций [{id,name}, ...] или простые значения
 *   value: начальное значение (для 'single'/'switch') или массив для 'list'
 *   onAccept/onClear/onOpen/onClose/onChange
 */
export function filter($container, options = {}) {
    const {
        type = 'list',
        filterFor = '',
        title = '',
        name = '',
        data = [],
        value = (type === 'list' ? [] : null),
        onAccept = null,
        onClear = null,
        onOpen = null,
        onClose = null,
        onChange = null,
        modClass = '',
    } = options;

    const $template = document.querySelector(`[${_attr('template-filter')}]`);
    if (!$template) {
        throw new Error('Template not found: use <template data-filter-template-filter> in DOM.');
    }

    // internal state
    let rootEl = null;
    let headerEl = null;
    let titleEl = null;
    let bodyEl = null;
    let acceptBtn = null;
    let resetBtn = null;
    let bodyListEl = null;
    let closeBtn = null;
    let optionEls = new Map(); // id -> element
    let selection = new Set(); // for list
    let singleValue = null; // for single
    // For switch we support two modes:
    //  - options provided (several optionEls with ids) -> selectedSwitchId stores the chosen id (string)
    //  - single switch element (id 'switch') -> switchBool stores boolean state
    let selectedSwitchId = null;
    let switchBool = false;
    let isOpen = false;
    let listeners = [];

    // render
    renderFilter();
    bindEvents();
    // set initial value
    setValue(value, { silent: true });

    // expose api
    const api = {
        open,
        close,
        toggle,
        getValue,
        setValue,
        reset,
        destroy,
        element: () => rootEl,
    };

    return api;

    // -------------- implementation --------------

    function renderFilter() {
        const fragment = $template.content.cloneNode(true);
        rootEl = fragment.querySelector(`[${_attr('filter')}]`);
        if (!rootEl) {
            rootEl = fragment.firstElementChild;
            if (!rootEl) throw new Error('Template root element not found');
        }

        if(options.modClass) {
            rootEl.classList.add(options.modClass);
        }

        // attributes
        rootEl.setAttribute(_attr('type'), type);
        if (filterFor) rootEl.setAttribute(_attr('for'), filterFor);
        if (name) rootEl.setAttribute('data-filter-name', name);

        titleEl = rootEl.querySelector(`[${_attr('title')}]`);
        if (!titleEl) titleEl = rootEl.querySelector('span') || rootEl;
        if (title) titleEl.textContent = title;

        // Найдём body/header/btns
        bodyEl = rootEl.querySelector(`.${classPrefix}__body`);
        headerEl = rootEl.querySelector(`[${_attr('header')}]`);
        acceptBtn = rootEl.querySelector(`[${_attr('filter-accept')}]`);
        resetBtn = rootEl.querySelector(`[${_attr('filter-reset')}]`);
        closeBtn = rootEl.querySelector(`[${_attr('filter-close')}]`);
        bodyListEl = rootEl.querySelector(`.${classPrefix}__body-list`);

        populateOptions(data);

        // body hidden by default
        if (bodyEl) {
            bodyEl.style.display = 'none';
        }

        $container.appendChild(fragment);
    }

    function populateOptions(items = []) {
        let targetList = rootEl.querySelector(`.${classPrefix}__filter-body-item-list`) ||
            rootEl.querySelector(`.${classPrefix}__body-list`) ||
            rootEl.querySelector(`.${classPrefix}__body-item-list`);

        if (!targetList) {
            targetList = document.createElement('div');
            targetList.className = `${classPrefix}__body-item-list`;
            const bodyList = rootEl.querySelector(`.${classPrefix}__body-list`) || rootEl;
            bodyList.appendChild(targetList);
        }

        targetList.innerHTML = '';
        optionEls.clear();

        if (type === 'list') {
            const allEl = createOptionElement('all', typeof titleEl?.textContent === 'string' ? titleEl.textContent : 'Все');
            allEl.classList.add('active'); // default
            targetList.appendChild(allEl);
            optionEls.set('all', allEl);
        }

        items.forEach((it) => {
            let id, label;
            if (it && typeof it === 'object' && it.hasOwnProperty('id')) {
                id = String(it.id);
                label = it.name ?? it.label ?? id;
            } else {
                id = String(it);
                label = String(it);
            }
            const el = createOptionElement(id, label);
            targetList.appendChild(el);
            optionEls.set(id, el);
        });

        // if switch type and no explicit item passed, create one toggle element labelled by title/name
        if (type === 'switch' && items.length === 0) {
            const el = createOptionElement('switch', name || title || 'Вкл/Выкл');
            el.setAttribute('role', 'switch');
            optionEls.set('switch', el);
            targetList.appendChild(el);
        }

        if (!bodyListEl) {
            bodyListEl = rootEl.querySelector(`.${classPrefix}__body-list`);
        }

        // После (пере)рендера опций — обновим видимость resetBtn (если list)
        updateResetBtnVisibility();
    }

    function createOptionElement(id, label) {
        const el = document.createElement('div');
        el.setAttribute(_attr('filter-option'), id);
        el.className = `${classPrefix}__body-tag`;
        el.dataset.filterOption = id;
        el.tabIndex = 0;
        el.textContent = label;
        return el;
    }

    function bindEvents() {
        if (!rootEl) return;
        headerEl = rootEl.querySelector(`[${_attr('header')}]`);
        bodyEl = rootEl.querySelector(`.${classPrefix}__body`);
        acceptBtn = rootEl.querySelector(`[${_attr('filter-accept')}]`);
        resetBtn = rootEl.querySelector(`[${_attr('filter-reset')}]`);
        closeBtn = rootEl.querySelector(`[${_attr('filter-close')}]`);

        if (headerEl) {
            headerEl.addEventListener('click', headerClickHandler);
            listeners.push({ el: headerEl, type: 'click', handler: headerClickHandler });
        }

        // option clicks
        optionEls.forEach((el, id) => {
            const handler = (e) => {
                e.stopPropagation();
                handleOptionClick(id, el, e);
            };
            const keyHandler = (ev) => {
                if (ev.key === 'Enter' || ev.key === ' ') {
                    ev.preventDefault();
                    handleOptionClick(id, el, ev);
                }
            };
            el.addEventListener('click', handler);
            el.addEventListener('keydown', keyHandler);
            listeners.push({ el, type: 'click', handler });
            listeners.push({ el, type: 'keydown', handler: keyHandler });
        });

        if (acceptBtn) {
            const handler = (e) => { e.stopPropagation(); accept(); };
            acceptBtn.addEventListener('click', handler);
            listeners.push({ el: acceptBtn, type: 'click', handler });
        }
        if (resetBtn) {
            const handler = (e) => { e.stopPropagation(); reset(); };
            resetBtn.addEventListener('click', handler);
            listeners.push({ el: resetBtn, type: 'click', handler });
        }
        if (closeBtn) {
            const handler = (e) => { e.stopPropagation(); close(); };
            closeBtn.addEventListener('click', handler);
            listeners.push({ el: closeBtn, type: 'click', handler });
        }

        // outside click closes
        const outsideClick = (e) => {
            if (!rootEl) return;
            if (!rootEl.contains(e.target) && isOpen) {
                close();
            }
        };
        document.addEventListener('click', outsideClick);
        listeners.push({ el: document, type: 'click', handler: outsideClick });

        // esc
        const keyHandler = (e) => { if (e.key === 'Escape' && isOpen) close(); };
        document.addEventListener('keydown', keyHandler);
        listeners.push({ el: document, type: 'keydown', handler: keyHandler });

        // touch outside (mobile)
        const touchHandler = (e) => {
            if (!rootEl) return;
            if (!rootEl.contains(e.target) && isOpen) close();
        };
        document.addEventListener('touchstart', touchHandler);
        listeners.push({ el: document, type: 'touchstart', handler: touchHandler });

        // Обновить видимость resetBtn на первоначальном этапе (если list)
        updateResetBtnVisibility();
    }

    function headerClickHandler(e) {
        e.stopPropagation();
        // Для switch: не открываем body, а переключаем значение
        if (type === 'switch') {
            // переключаем на следующую опцию (или инвертируем bool для одиночного switch)
            headerToggleSwitch();
            return;
        }

        // Открываем только для list или single
        if (type === 'list' || type === 'single') {
            toggle();
        } else {
            toggle();
        }
    }

    function headerToggleSwitch() {
        // Получаем ключи опций, исключая служебные 'all'
        const keys = Array.from(optionEls.keys()).filter(k => k !== 'all');
        // Если есть более одной опции (например '1' и '0') — переключаем между ними по кругу
        const switchKeys = keys.filter(k => k !== 'switch');
        if (switchKeys.length >= 2) {
            const current = selectedSwitchId !== null ? selectedSwitchId : findActiveOptionId(switchKeys);
            const idx = switchKeys.indexOf(current);
            const nextIdx = idx === -1 ? 0 : (idx + 1) % switchKeys.length;
            const nextId = switchKeys[nextIdx];
            // применяем и сразу подтверждаем
            setValue(nextId);
            // auto-accept UX
            accept();
            return;
        }

        // Если есть одиночный элемент с id 'switch' — просто инвертируем булево состояние
        const singleSwitchEl = optionEls.get('switch') || (switchKeys.length === 1 ? optionEls.get(switchKeys[0]) : null);
        if (singleSwitchEl) {
            // инвертируем
            if (switchKeys.length === 1) {
                // переключаем между двумя явными опциями (если только одна явная — переключаем на неё / с неё)
                const onlyId = switchKeys[0];
                const currentId = selectedSwitchId || findActiveOptionId([onlyId]);
                if (currentId === onlyId) {
                    // если сейчас выбран onlyId — убираем выбор (или ставим false)
                    setValue(null);
                } else {
                    setValue(onlyId);
                }
            } else {
                // используем булево для 'switch' элемента
                switchBool = !switchBool;
                updateSwitchUiByBool();
                triggerChange();
                accept();
            }
            return;
        }

        // fallback: ничего не делать
    }

    function findActiveOptionId(keys) {
        for (const k of keys) {
            const el = optionEls.get(k);
            if (el && el.classList.contains('active')) return k;
        }
        return null;
    }

    function handleOptionClick(id, el, event) {
        if (type === 'list') {
            if (id === 'all') {
                selection.clear();
                optionEls.forEach((opEl, opId) => {
                    if (opId === 'all') opEl.classList.add('active');
                    else opEl.classList.remove('active');
                });
                selection.add('all');
                updateHeaderTitle();
                updateResetBtnVisibility();
                triggerChange();
                return;
            }
            const currentlyActive = el.classList.contains('active');
            if (currentlyActive) {
                el.classList.remove('active');
                selection.delete(id);
                if (selection.size === 0) {
                    const allEl = optionEls.get('all');
                    if (allEl) {
                        allEl.classList.add('active');
                        selection.add('all');
                    }
                }
            } else {
                el.classList.add('active');
                selection.add(id);
                const allEl = optionEls.get('all');
                if (allEl) {
                    allEl.classList.remove('active');
                    selection.delete('all');
                }
            }
            updateHeaderTitle();
            updateResetBtnVisibility();
            triggerChange();

        } else if (type === 'single') {
            optionEls.forEach((opEl, opId) => {
                if (opEl === el) {
                    opEl.classList.add('active');
                    singleValue = opId;
                } else {
                    opEl.classList.remove('active');
                }
            });
            updateHeaderTitle();
            triggerChange();
            accept();

        } else if (type === 'switch') {
            // если клик по конкретной опции — выбираем её
            // поддерживаем как явные опции (например '1'/'0'), так и единичный switch
            const keys = Array.from(optionEls.keys()).filter(k => k !== 'all');
            const explicitKeys = keys.filter(k => k !== 'switch');
            if (explicitKeys.length > 0) {
                // выбрать конкретный id
                selectedSwitchId = id;
                // UI: отметить только эту опцию активной
                optionEls.forEach((opEl, opId) => {
                    if (opId === id) opEl.classList.add('active');
                    else opEl.classList.remove('active');
                });
                // обновим булевое значение для совместимости (если id числовой)
                const num = Number(id);
                switchBool = !Number.isNaN(num) ? Boolean(num) : Boolean(id);
                updateHeaderTitle();
                triggerChange();
                accept();
            } else {
                // единичный switch элемент
                switchBool = !switchBool;
                updateSwitchUiByBool();
                updateHeaderTitle();
                triggerChange();
                accept();
            }
        }
    }

    function updateSwitchUiByBool() {
        // если в DOM есть опция с id 'switch' — помечаем актив/неактив
        const switchEl = optionEls.get('switch');
        if (switchEl) {
            if (switchBool) switchEl.classList.add('active'); else switchEl.classList.remove('active');
        }
        // если есть явные опции (например '1'/'0'), пометим по булевому (ищем id '1' или '0')
        const explicitKeys = Array.from(optionEls.keys()).filter(k => k !== 'all' && k !== 'switch');
        if (explicitKeys.length > 0) {
            // если среди ключей есть '1' и '0' — используем их
            const preferId = switchBool ? explicitKeys.find(k => k === '1') || explicitKeys[0] : explicitKeys.find(k => k === '0') || explicitKeys[0];
            optionEls.forEach((el, id) => {
                if (id === preferId) el.classList.add('active'); else el.classList.remove('active');
            });
            selectedSwitchId = preferId;
        }
    }

    // NEW: скрывать/показывать resetBtn для типа list
    function updateResetBtnVisibility() {
        if (type !== 'list') return;
        if (!resetBtn) return;
        // считаем выбранные, исключая 'all'
        const selectedExclAll = Array.from(selection).filter(id => id !== 'all');
        const hasExplicitSelection = selectedExclAll.length > 0;
        // если нет явного выбора — скрываем resetBtn
        if (!hasExplicitSelection) {
            // скрыть
            resetBtn.style.display = 'none';
        } else {
            // показать (сбрасываем стиль)
            resetBtn.style.display = '';
        }
    }

    function accept() {
        const val = getValue();
        if (typeof onAccept === 'function') {
            try { onAccept(val); } catch (err) { console.error(err); }
        }
        close();
    }

    function open() {
        if (!rootEl || isOpen) return;
        rootEl.classList.add('open', 'active');
        if (bodyEl) {
            bodyEl.style.display = 'flex';
        }
        isOpen = true;
        if (typeof onOpen === 'function') onOpen();
    }

    function close() {
        if (!rootEl || !isOpen) return;
        rootEl.classList.remove('open');
        if (bodyEl) {
            bodyEl.style.display = 'none';
        }
        isOpen = false;
        if (typeof onClose === 'function') onClose();
    }

    function toggle() {
        if (isOpen) close();
        else open();
    }

    function updateHeaderTitle() {
        if (!titleEl) return;
        if (type === 'list') {
            titleEl.textContent = title || name || titleEl.dataset.defaultTitle || titleEl.textContent;
            // базовый заголовок (принять priority: options.title -> name -> data-default -> текущее)
            let baseTitle = (title || name || titleEl.dataset.defaultTitle || titleEl.textContent || '').trim();

            // удалить ведущий префикс "Все " или "Все:" (без учёта регистра), чтобы "Все запросы" -> "Запросы"
            baseTitle = baseTitle.replace(/^\s*все[:\s-]+/i, '').trim();

            // вычислим количество выбранных, исключая 'all'
            const selectedIds = Array.from(selection).filter(id => id !== 'all');
            const selectedCount = selectedIds.length;

            if (selectedCount === 0) {
                // нет явного выбора — показываем базовый заголовок (например "Запросы")
                titleEl.textContent = baseTitle || titleEl.textContent;
            } else {
                // если что-то выбрано — показываем просто число выбранных (требование)
                titleEl.textContent = baseTitle + ': ' + String(selectedCount);
            }

        } else if (type === 'single') {
            if (!singleValue) {
                titleEl.textContent = title || name || titleEl.textContent;
            } else {
                const el = optionEls.get(singleValue);
                titleEl.textContent = el ? el.textContent : String(singleValue);
            }
        } else if (type === 'switch') {
            // always show prefix (title/name) + ': ' + state (lowercase)
            const prefix = (name || title || (titleEl ? titleEl.textContent : '')).trim();

            // If an explicit option id is selected, show its label lowercased
            if (selectedSwitchId) {
                const el = optionEls.get(selectedSwitchId);
                if (el) {
                    titleEl.textContent = `${prefix}: ${String(el.textContent).trim().toLowerCase()}`;
                    return;
                }
            }

            // If there's any explicit active option (e.g. '1'/'0'), show its label lowercased
            const activeExplicit = Array.from(optionEls.entries()).find(([k, el]) => k !== 'switch' && el.classList.contains('active'));
            if (activeExplicit) {
                titleEl.textContent = `${prefix}: ${String(activeExplicit[1].textContent).trim().toLowerCase()}`;
                return;
            }

            // Fallback for single 'switch' element — show 'вкл' / 'выкл'
            titleEl.textContent = `${prefix}: ${switchBool ? 'вкл' : 'выкл'}`;
        }
    }

    function triggerChange() {
        if (typeof onChange === 'function') {
            try { onChange(getValue()); } catch (err) { console.error(err); }
        }
    }

    // API functions
    function getValue() {
        if (type === 'list') {
            const arr = Array.from(selection);
            if (arr.length === 1 && arr[0] === 'all') return [];
            return arr;
        } else if (type === 'single') {
            return singleValue;
        } else if (type === 'switch') {
            // если явно есть опция (например '1'/'0') — возвращаем её id (число если возможно)
            const explicitKeys = Array.from(optionEls.keys()).filter(k => k !== 'all' && k !== 'switch');
            if (explicitKeys.length > 0) {
                const active = selectedSwitchId || findActiveOptionId(explicitKeys);
                if (active === null) return null;
                // если число — вернуть Number, иначе строку
                if (/^-?\d+$/.test(active)) return Number(active);
                return active;
            }
            // иначе возвращаем булево
            return switchBool;
        }
        return null;
    }

    function setValue(v, opts = {}) {
        const { silent = false } = opts;
        if (type === 'list') {
            selection.clear();
            optionEls.forEach((el) => el.classList.remove('active'));
            if (!v || (Array.isArray(v) && v.length === 0)) {
                const allEl = optionEls.get('all');
                if (allEl) {
                    allEl.classList.add('active');
                    selection.add('all');
                }
            } else {
                const arr = Array.isArray(v) ? v : [v];
                arr.forEach(val => {
                    const id = String(val);
                    const el = optionEls.get(id);
                    if (el) {
                        el.classList.add('active');
                        selection.add(id);
                    }
                });
                const allEl = optionEls.get('all');
                if (allEl) allEl.classList.remove('active');
            }
            updateHeaderTitle();
            updateResetBtnVisibility();
            if (!silent) triggerChange();

        } else if (type === 'single') {
            singleValue = v != null ? String(v) : null;
            optionEls.forEach((el, id) => {
                if (id === singleValue) el.classList.add('active'); else el.classList.remove('active');
            });
            updateHeaderTitle();
            if (!silent) triggerChange();

        } else if (type === 'switch') {
            // normalize: if v is boolean -> use switchBool and clear explicit id
            const explicitKeys = Array.from(optionEls.keys()).filter(k => k !== 'all' && k !== 'switch');
            if (typeof v === 'boolean') {
                switchBool = v;
                selectedSwitchId = null;
                updateSwitchUiByBool();
            } else if (v == null) {
                // clear
                selectedSwitchId = null;
                switchBool = false;
                optionEls.forEach(el => el.classList.remove('active'));
            } else {
                // если передали строку/число - используем как id (если такой есть)
                const s = String(v);
                if (optionEls.has(s)) {
                    selectedSwitchId = s;
                    optionEls.forEach((el, id) => {
                        if (id === s) el.classList.add('active'); else el.classList.remove('active');
                    });
                    // обновим булевое по числу если применимо
                    const n = Number(s);
                    switchBool = !Number.isNaN(n) ? Boolean(n) : Boolean(s);
                } else {
                    // fallback: попытка привести к числу и найти совпадение ('1'/'0')
                    const n = Number(v);
                    if (!Number.isNaN(n)) {
                        const candidate = String(n);
                        if (optionEls.has(candidate)) {
                            selectedSwitchId = candidate;
                            optionEls.forEach((el, id) => {
                                if (id === candidate) el.classList.add('active'); else el.classList.remove('active');
                            });
                            switchBool = Boolean(n);
                        } else {
                            // просто выставим bool
                            switchBool = Boolean(n);
                            updateSwitchUiByBool();
                        }
                    } else {
                        // неизвестный формат -> привести к булевому и обновить UI
                        switchBool = Boolean(v);
                        updateSwitchUiByBool();
                    }
                }
            }
            updateHeaderTitle();
            if (!silent) triggerChange();
        }
    }

    function reset() {
        // reset state to default
        selection.clear();
        singleValue = null;
        selectedSwitchId = null;
        switchBool = false;
        optionEls.forEach((el) => el.classList.remove('active'));
        if (type === 'list') {
            const allEl = optionEls.get('all');
            if (allEl) {
                allEl.classList.add('active');
                selection.add('all');
            }
        }
        updateHeaderTitle();
        updateResetBtnVisibility();
        if (typeof onClear === 'function') {
            try { onClear(); } catch (err) { console.error(err); }
        }
        triggerChange();
    }

    function destroy() {
        listeners.forEach(({ el, type, handler }) => {
            try { el.removeEventListener(type, handler); } catch (e) { /*ignore*/ }
        });
        listeners = [];
        if (rootEl && rootEl.parentNode) rootEl.parentNode.removeChild(rootEl);
        rootEl = null;
        optionEls.clear();
    }
}
